import React, { useState, useEffect, useRef } from "react";
import { useApi } from "../../../hooks/use-api";
import { useQuestionBankHandler } from "../../question-bank/handler";
import {
  apiTestExams,
  apiTestClasses,
  apiTestChapters,
  apiTestSubTopics,
} from "../../../utils/api-routes";
import { getChapterDisplayLabel } from "../../../utils/chapter-mapping";
import { COOKIE_ACCESS_MAIL } from "../../../utils/storage-constants";
import "./style.sass";

export const ImportPopup = ({ onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cascading dropdown states (Zero mock data, 100% live API data)
  const [examOptions, setExamOptions] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [loadingExams, setLoadingExams] = useState(false);

  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [loadingClasses, setLoadingClasses] = useState(false);

  const [chapterOptions, setChapterOptions] = useState([]);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);

  const [conceptOptions, setConceptOptions] = useState([]);
  const [selectedConcepts, setSelectedConcepts] = useState([]);
  const [loadingConcepts, setLoadingConcepts] = useState(false);

  const [levels] = useState(["Easy", "Medium", "Hard"]);
  const [selectedLevel, setSelectedLevel] = useState("");

  const [openDropdown, setOpenDropdown] = useState(null); // 'exam' | 'class' | 'chapter' | 'concept' | 'level' | null

  const fileInputRef = useRef(null);
  const api = useApi();
  const { uploadFileV2, uploadFile } = useQuestionBankHandler(api);

  const teacherMail =
    typeof window !== "undefined"
      ? localStorage.getItem(COOKIE_ACCESS_MAIL) || "varsha.r.apr3@gmail.com"
      : "varsha.r.apr3@gmail.com";
  const teacherRole = "teacher";

  // Dismiss dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest(".qb_import_form_group_top")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // 1. Fetch Exams from live API: /teacher/test/exams?email=...&role=teacher
  useEffect(() => {
    let isMounted = true;
    if (!teacherMail) return;
    const fetchExams = async () => {
      setLoadingExams(true);
      try {
        const res = await api.get(apiTestExams(teacherMail, teacherRole));
        if (!isMounted) return;
        const data =
          res?.data?.exams ||
          res?.exams ||
          (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
        const list = Array.isArray(data)
          ? Array.from(
              new Set(
                data
                  .map((item) =>
                    typeof item === "string"
                      ? item
                      : item.name || item.exam || item.value || ""
                  )
                  .filter(Boolean)
              )
            )
          : [];
        setExamOptions(list);
      } catch (err) {
        console.error("Error fetching exams from /teacher/test/exams:", err);
        if (isMounted) setExamOptions([]);
      } finally {
        if (isMounted) setLoadingExams(false);
      }
    };
    fetchExams();
    return () => {
      isMounted = false;
    };
  }, [teacherMail]);

  // 2. Fetch Classes from live API: /teacher/test/classes?exam=...&email=...&role=teacher
  useEffect(() => {
    let isMounted = true;
    if (!selectedExam || !teacherMail) {
      setClassOptions([]);
      setSelectedClass("");
      setChapterOptions([]);
      setSelectedChapters([]);
      setConceptOptions([]);
      setSelectedConcepts([]);
      return;
    }
    const fetchClasses = async () => {
      setLoadingClasses(true);
      try {
        const res = await api.get(
          apiTestClasses(selectedExam, teacherMail, teacherRole)
        );
        if (!isMounted) return;
        const data =
          res?.data?.classes ||
          res?.classes ||
          (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
        const list = Array.isArray(data)
          ? Array.from(
              new Set(
                data
                  .map((item) =>
                    typeof item === "string"
                      ? item
                      : item.name || item.class || item.value || ""
                  )
                  .filter(Boolean)
              )
            )
          : [];
        setClassOptions(list);
      } catch (err) {
        console.error("Error fetching classes from /teacher/test/classes:", err);
        if (isMounted) setClassOptions([]);
      } finally {
        if (isMounted) setLoadingClasses(false);
      }
    };
    fetchClasses();
    return () => {
      isMounted = false;
    };
  }, [selectedExam, teacherMail]);

  // 3. Fetch Chapters from live API: /teacher/test/chapters?exam=...&class=...&email=...&role=teacher
  useEffect(() => {
    let isMounted = true;
    if (!selectedExam || !selectedClass || !teacherMail) {
      setChapterOptions([]);
      setSelectedChapters([]);
      setConceptOptions([]);
      setSelectedConcepts([]);
      return;
    }
    const fetchChapters = async () => {
      setLoadingChapters(true);
      try {
        const res = await api.get(
          apiTestChapters(selectedExam, selectedClass, teacherMail, teacherRole)
        );
        if (!isMounted) return;
        const data =
          res?.data?.chapters ||
          res?.chapters ||
          (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
        const list = Array.isArray(data)
          ? Array.from(
              new Set(
                data
                  .map((item) =>
                    typeof item === "string"
                      ? item
                      : item.name || item.chapter || item.value || ""
                  )
                  .filter(Boolean)
              )
            )
          : [];
        setChapterOptions(list);
      } catch (err) {
        console.error("Error fetching chapters from /teacher/test/chapters:", err);
        if (isMounted) setChapterOptions([]);
      } finally {
        if (isMounted) setLoadingChapters(false);
      }
    };
    fetchChapters();
    return () => {
      isMounted = false;
    };
  }, [selectedExam, selectedClass, teacherMail]);

  // 4. Fetch Sub Topics / Concepts from live API: /teacher/test/sub-topics?exam=...&chapter=...&email=...&role=teacher
  useEffect(() => {
    let isMounted = true;
    if (!selectedExam || selectedChapters.length === 0 || !teacherMail) {
      setConceptOptions([]);
      setSelectedConcepts([]);
      return;
    }
    const fetchConcepts = async () => {
      setLoadingConcepts(true);
      try {
        let allConcepts = [];
        try {
          const res = await api.get(
            apiTestSubTopics(selectedExam, selectedChapters, teacherMail, teacherRole)
          );
          const data =
            res?.data?.sub_topics ||
            res?.data?.subTopics ||
            res?.sub_topics ||
            res?.subTopics ||
            (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
          if (Array.isArray(data) && data.length > 0) {
            allConcepts = data
              .map((item) =>
                typeof item === "string"
                  ? item
                  : item.name || item.sub_topic || item.topic || item.value || ""
              )
              .filter(Boolean);
          }
        } catch (singleErr) {
          console.warn("Combined sub-topics fetch failed, falling back to per-chapter fetch:", singleErr);
        }

        if (allConcepts.length === 0 && selectedChapters.length > 0) {
          const results = await Promise.all(
            selectedChapters.map((ch) =>
              api
                .get(apiTestSubTopics(selectedExam, ch, teacherMail, teacherRole))
                .then((res) => {
                  const data =
                    res?.data?.sub_topics ||
                    res?.data?.subTopics ||
                    res?.sub_topics ||
                    res?.subTopics ||
                    (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
                  return Array.isArray(data) ? data : [];
                })
                .catch(() => [])
            )
          );
          const flattened = results
            .flat()
            .map((item) =>
              typeof item === "string"
                ? item
                : item.name || item.sub_topic || item.topic || item.value || ""
            )
            .filter(Boolean);
          allConcepts = flattened;
        }

        if (!isMounted) return;
        const uniqueList = Array.from(new Set(allConcepts));
        setConceptOptions(uniqueList);
        setSelectedConcepts((prev) => prev.filter((c) => uniqueList.includes(c)));
      } catch (err) {
        console.error("Error fetching sub-topics from /teacher/test/sub-topics:", err);
        if (isMounted) setConceptOptions([]);
      } finally {
        if (isMounted) setLoadingConcepts(false);
      }
    };
    fetchConcepts();
    return () => {
      isMounted = false;
    };
  }, [selectedExam, selectedChapters, teacherMail]);

  const handleFile = (selectedFile) => {
    if (
      selectedFile &&
      (selectedFile.name.endsWith(".xlsx") ||
        selectedFile.name.endsWith(".xls") ||
        selectedFile.name.endsWith(".csv") ||
        selectedFile.name.endsWith(".pdf"))
    ) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError("Please select a valid file (.pdf, .xlsx, .xls, .csv)");
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleChapterToggle = (chapter) => {
    setSelectedChapters((prev) =>
      prev.includes(chapter)
        ? prev.filter((ch) => ch !== chapter)
        : [...prev, chapter]
    );
    setError(null);
  };

  const handleSelectAllChapters = () => {
    if (selectedChapters.length === chapterOptions.length) {
      setSelectedChapters([]);
    } else {
      setSelectedChapters([...chapterOptions]);
    }
    setError(null);
  };

  const handleConceptToggle = (concept) => {
    setSelectedConcepts((prev) =>
      prev.includes(concept)
        ? prev.filter((c) => c !== concept)
        : [...prev, concept]
    );
    setError(null);
  };

  const handleSelectAllConcepts = () => {
    if (selectedConcepts.length === conceptOptions.length) {
      setSelectedConcepts([]);
    } else {
      setSelectedConcepts([...conceptOptions]);
    }
    setError(null);
  };

  const getChapterDisplayText = () => {
    if (selectedChapters.length === 0) return "";
    if (selectedChapters.length === 1) return getChapterDisplayLabel(selectedChapters[0]);
    if (selectedChapters.length === chapterOptions.length) {
      return `All Chapters (${selectedChapters.length})`;
    }
    return `${selectedChapters.length} Chapters Selected`;
  };

  const getChapterTooltip = () => {
    if (selectedChapters.length === 0) return "";
    return selectedChapters.map(getChapterDisplayLabel).join(", ");
  };

  const getConceptDisplayText = () => {
    if (selectedConcepts.length === 0) return "";
    if (selectedConcepts.length === 1) return selectedConcepts[0];
    if (selectedConcepts.length === conceptOptions.length) {
      return `All Concepts (${selectedConcepts.length})`;
    }
    return `${selectedConcepts.length} Concepts Selected`;
  };

  const getConceptTooltip = () => {
    if (selectedConcepts.length === 0) return "";
    return selectedConcepts.join(", ");
  };

  const isFormValid =
    Boolean(file) &&
    Boolean(selectedExam) &&
    Boolean(selectedClass) &&
    selectedChapters.length > 0 &&
    Boolean(selectedLevel) &&
    !loading;

  const handleUpload = async () => {
    if (!selectedExam) {
      setError("Please select an exam.");
      return;
    }
    if (!selectedClass) {
      setError("Please select a class.");
      return;
    }
    if (selectedChapters.length === 0) {
      setError("Please select at least one chapter.");
      return;
    }
    if (!selectedLevel) {
      setError("Please select a difficulty level (Easy, Medium, or Hard).");
      return;
    }
    if (!file) {
      setError("Please select a file to upload.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const defaultTestName = `sample_test_${new Date().toISOString()}`;
      const defaultNoOfQuestions = 1;
      const uploader = uploadFileV2 || uploadFile;
      const res = await uploader(file, {
        testName: defaultTestName,
        noOfQuestions: defaultNoOfQuestions,
        level: selectedLevel,
        exam: selectedExam,
        clas: selectedClass,
        chapter: selectedChapters.join(","),
        chapters: selectedChapters,
        concept: selectedConcepts.join(","),
        concepts: selectedConcepts,
        isV2: true,
      });
      if (onUploadSuccess) {
        onUploadSuccess(res || {}, {
          testName: defaultTestName,
          noOfQuestions: defaultNoOfQuestions,
          level: selectedLevel,
          exam: selectedExam,
          clas: selectedClass,
          chapter: selectedChapters.join(","),
          chapters: selectedChapters,
          concept: selectedConcepts.join(","),
          concepts: selectedConcepts,
        });
      }
      onClose();
    } catch (err) {
      const errorDetail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to upload file. Please try again.";
      setError(typeof errorDetail === "string" ? errorDetail : JSON.stringify(errorDetail));
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <>
      {loading && (
        <div className="kb_processing_overlay">
          <div className="kb_processing_box">
            <div className="kb_spinner">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p className="kb_processing_text">
              Processing your content and updating the question bank. Please stay on this page. Once completed, you can conduct a test using Test Management.
            </p>
          </div>
        </div>
      )}

      <div className="import_popup_overlay" onClick={onClose}>
        <div className="popup_box" onClick={(e) => e.stopPropagation()}>
          <button className="close_btn" onClick={onClose} aria-label="Close popup">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <h2>Import Questions</h2>
          <p className="popup_subtitle">Upload an Excel or PDF file to import questions</p>

          {error && (
            <div className="error_banner">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Row 1: Exam and Class */}
          <div className="qb_import_grid_row">
            {/* Exam Dropdown */}
            <div
              className={`qb_import_form_group_top ${openDropdown === "exam" ? "is_open" : ""}`}
              style={{ zIndex: 60 }}
            >
              <label>Exam <span className="required-star" style={{ color: "#EF4444", marginLeft: "3px" }}>*</span></label>
              <div className="qb_import_combobox_container">
                <input
                  className="qb_import_combobox_input"
                  type="text"
                  placeholder={loadingExams ? "Loading exams..." : "Select Exam..."}
                  value={selectedExam}
                  readOnly
                  onClick={() => setOpenDropdown(openDropdown === "exam" ? null : "exam")}
                  style={{ cursor: "pointer" }}
                />
                <svg
                  className={`qb_import_combobox_arrow ${openDropdown === "exam" ? "rotated" : ""}`}
                  onClick={() => setOpenDropdown(openDropdown === "exam" ? null : "exam")}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="2.2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>

                {openDropdown === "exam" && (
                  <div className="qb_import_combobox_menu">
                    <div className="qb_import_combobox_options">
                      {loadingExams ? (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          Loading exams...
                        </div>
                      ) : examOptions.length > 0 ? (
                        examOptions.map((opt, idx) => {
                          const isSelected = selectedExam === opt;
                          return (
                            <div
                              key={idx}
                              className={`qb_import_combobox_item ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                setSelectedExam(opt);
                                setSelectedClass("");
                                setSelectedChapters([]);
                                setSelectedConcepts([]);
                                setOpenDropdown(null);
                                setError(null);
                              }}
                            >
                              <span>{opt}</span>
                              {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          No exams available
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Class Dropdown */}
            <div
              className={`qb_import_form_group_top ${openDropdown === "class" ? "is_open" : ""}`}
              style={{ zIndex: 60 }}
            >
              <label>Class <span className="required-star" style={{ color: "#EF4444", marginLeft: "3px" }}>*</span></label>
              <div className="qb_import_combobox_container">
                <input
                  className={`qb_import_combobox_input ${!selectedExam ? "disabled" : ""}`}
                  type="text"
                  placeholder={
                    !selectedExam
                      ? "Select Exam first"
                      : loadingClasses
                      ? "Loading classes..."
                      : "Select Class..."
                  }
                  value={selectedClass}
                  readOnly
                  onClick={() => {
                    if (!selectedExam) return;
                    setOpenDropdown(openDropdown === "class" ? null : "class");
                  }}
                  style={{ cursor: selectedExam ? "pointer" : "not-allowed" }}
                />
                <svg
                  className={`qb_import_combobox_arrow ${openDropdown === "class" ? "rotated" : ""}`}
                  onClick={() => {
                    if (!selectedExam) return;
                    setOpenDropdown(openDropdown === "class" ? null : "class");
                  }}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="2.2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>

                {openDropdown === "class" && (
                  <div className="qb_import_combobox_menu">
                    <div className="qb_import_combobox_options">
                      {loadingClasses ? (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          Loading classes...
                        </div>
                      ) : classOptions.length > 0 ? (
                        classOptions.map((opt, idx) => {
                          const isSelected = selectedClass === opt;
                          return (
                            <div
                              key={idx}
                              className={`qb_import_combobox_item ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                setSelectedClass(opt);
                                setSelectedChapters([]);
                                setSelectedConcepts([]);
                                setOpenDropdown(null);
                                setError(null);
                              }}
                            >
                              <span>{opt}</span>
                              {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          No classes available
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Chapter and Concept (Both Multi-Select) */}
          <div className="qb_import_grid_row">
            {/* Chapter Multi-Select Dropdown */}
            <div
              className={`qb_import_form_group_top ${openDropdown === "chapter" ? "is_open" : ""}`}
              style={{ zIndex: 50 }}
            >
              <label>Chapter <span className="required-star" style={{ color: "#EF4444", marginLeft: "3px" }}>*</span></label>
              <div className="qb_import_combobox_container">
                <input
                  className={`qb_import_combobox_input ${!selectedClass ? "disabled" : ""}`}
                  type="text"
                  placeholder={
                    !selectedClass
                      ? "Select Class first"
                      : loadingChapters
                      ? "Loading chapters..."
                      : "Select Chapter..."
                  }
                  value={getChapterDisplayText()}
                  title={getChapterTooltip()}
                  readOnly
                  onClick={() => {
                    if (!selectedClass) return;
                    setOpenDropdown(openDropdown === "chapter" ? null : "chapter");
                  }}
                  style={{ cursor: selectedClass ? "pointer" : "not-allowed" }}
                />
                <svg
                  className={`qb_import_combobox_arrow ${openDropdown === "chapter" ? "rotated" : ""}`}
                  onClick={() => {
                    if (!selectedClass) return;
                    setOpenDropdown(openDropdown === "chapter" ? null : "chapter");
                  }}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="2.2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>

                {openDropdown === "chapter" && (
                  <div className="qb_import_combobox_menu">
                    <div className="qb_import_combobox_options">
                      {loadingChapters ? (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          Loading chapters...
                        </div>
                      ) : chapterOptions.length > 0 ? (
                        <>
                          <div
                            className={`qb_import_combobox_item select_all_item ${
                              selectedChapters.length === chapterOptions.length ? "selected" : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectAllChapters();
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                              <input
                                type="checkbox"
                                checked={selectedChapters.length === chapterOptions.length}
                                onChange={() => {}}
                                className="qb_custom_checkbox"
                              />
                              <span style={{ fontWeight: 700, color: "#612EF7" }}>
                                Select All ({chapterOptions.length})
                              </span>
                            </div>
                          </div>

                          {chapterOptions.map((opt, idx) => {
                            const isSelected = selectedChapters.includes(opt);
                            const displayLabel = getChapterDisplayLabel(opt);
                            return (
                              <div
                                key={idx}
                                className={`qb_import_combobox_item checkbox_item ${isSelected ? "selected" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChapterToggle(opt);
                                }}
                                title={opt}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="qb_custom_checkbox"
                                  />
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {displayLabel}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          No chapters available
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Concept Multi-Select Dropdown */}
            <div
              className={`qb_import_form_group_top ${openDropdown === "concept" ? "is_open" : ""}`}
              style={{ zIndex: 50 }}
            >
              <label>Concept</label>
              <div className="qb_import_combobox_container">
                <input
                  className={`qb_import_combobox_input ${selectedChapters.length === 0 || conceptOptions.length === 0 ? "disabled" : ""}`}
                  type="text"
                  placeholder={
                    selectedChapters.length === 0
                      ? "Select Chapter first"
                      : loadingConcepts
                      ? "Loading concepts..."
                      : conceptOptions.length === 0
                      ? "No concepts found"
                      : "Select Concept..."
                  }
                  value={getConceptDisplayText()}
                  title={getConceptTooltip()}
                  readOnly
                  onClick={() => {
                    if (selectedChapters.length === 0 || conceptOptions.length === 0) return;
                    setOpenDropdown(openDropdown === "concept" ? null : "concept");
                  }}
                  style={{
                    cursor: selectedChapters.length > 0 && conceptOptions.length > 0 ? "pointer" : "not-allowed",
                  }}
                />
                <svg
                  className={`qb_import_combobox_arrow ${openDropdown === "concept" ? "rotated" : ""}`}
                  onClick={() => {
                    if (selectedChapters.length === 0 || conceptOptions.length === 0) return;
                    setOpenDropdown(openDropdown === "concept" ? null : "concept");
                  }}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="2.2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>

                {openDropdown === "concept" && (
                  <div className="qb_import_combobox_menu">
                    <div className="qb_import_combobox_options">
                      {loadingConcepts ? (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          Loading concepts...
                        </div>
                      ) : conceptOptions.length > 0 ? (
                        <>
                          <div
                            className={`qb_import_combobox_item select_all_item ${
                              selectedConcepts.length === conceptOptions.length ? "selected" : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectAllConcepts();
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                              <input
                                type="checkbox"
                                checked={selectedConcepts.length === conceptOptions.length}
                                onChange={() => {}}
                                className="qb_custom_checkbox"
                              />
                              <span style={{ fontWeight: 700, color: "#612EF7" }}>
                                Select All ({conceptOptions.length})
                              </span>
                            </div>
                          </div>

                          {conceptOptions.map((opt, idx) => {
                            const isSelected = selectedConcepts.includes(opt);
                            return (
                              <div
                                key={idx}
                                className={`qb_import_combobox_item checkbox_item ${isSelected ? "selected" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConceptToggle(opt);
                                }}
                                title={opt}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="qb_custom_checkbox"
                                  />
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {opt}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="qb_import_combobox_item disabled" style={{ cursor: "default", color: "#9CA3AF" }}>
                          No concepts available
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Level Selection Dropdown */}
          <div
            className={`qb_import_form_group_top level_group ${openDropdown === "level" ? "is_open" : ""}`}
            style={{ zIndex: 40 }}
          >
            <label>Level <span className="required-star" style={{ color: "#EF4444", marginLeft: "3px" }}>*</span></label>
            <div className="qb_import_combobox_container">
              <input
                className="qb_import_combobox_input"
                type="text"
                placeholder="Select Level..."
                value={selectedLevel}
                readOnly
                onClick={() => setOpenDropdown(openDropdown === "level" ? null : "level")}
                style={{ cursor: "pointer" }}
              />
              <svg
                className={`qb_import_combobox_arrow ${openDropdown === "level" ? "rotated" : ""}`}
                onClick={() => setOpenDropdown(openDropdown === "level" ? null : "level")}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6B7280"
                strokeWidth="2.2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>

              {openDropdown === "level" && (
                <div className="qb_import_combobox_menu level_menu">
                  <div className="qb_import_combobox_options">
                    {levels.map((lvl, idx) => {
                      const isSelected = selectedLevel.toLowerCase() === lvl.toLowerCase();
                      const dotColor =
                        lvl.toLowerCase() === "easy"
                          ? "#10B981"
                          : lvl.toLowerCase() === "medium"
                            ? "#F59E0B"
                            : "#EF4444";
                      return (
                        <div
                          key={idx}
                          className={`qb_import_combobox_item ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            setSelectedLevel(lvl);
                            setOpenDropdown(null);
                            setError(null);
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: dotColor,
                                flexShrink: 0,
                              }}
                            />
                            <span>{lvl}</span>
                          </div>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upload Area */}
          <div
            className={`upload_area ${dragActive ? "active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{ zIndex: 10 }}
          >
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx, .xls, .csv, .pdf"
              onChange={handleChange}
            />
            <div className="upload_icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#612EF7"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            {!file ? (
              <>
                <div className="upload_title">Question Bank</div>
                <div className="supported_text">Supported format: .pdf, .xlsx, .xls, .csv — max 10 MB</div>
              </>
            ) : (
              <>
                <div className="upload_title" style={{ color: "#612EF7" }}>{file.name}</div>
                <div className="supported_text">
                  Click or drag to change file ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                </div>
              </>
            )}
          </div>

          <div className="popup_actions">
            <button className="cancel_btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              className={`upload_btn ${isFormValid ? "active" : ""}`}
              onClick={handleUpload}
              disabled={loading || !isFormValid}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {loading ? "Uploading..." : "Proceed →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
